import AttachFileIcon from "@mui/icons-material/AttachFile";
import ClearIcon from "@mui/icons-material/Clear";
import CloudDownload from "@mui/icons-material/CloudDownload";
import ListIcon from "@mui/icons-material/List";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  DialogActions,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import ContextOrganizationField from "components/ContextOrganizationField";
import ImportsStepper from "./ImportsStepper";
import RedirectBackButton from "components/RedirectBackButton";
import SelectEntity from "components/SelectEntity";
import SnackbarMessage from "components/SnackbarMessage";
import { API_ORGANIZATION_ID } from "config/headers";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { DropzoneAreaBase, FileObject } from "mui-file-dropzone";
import { useEffect, useMemo, useState } from "react";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ImportJobType,
  JobTypeFilter,
  ListRequestOfJobTypeFilter,
  ListResponseOfImportJobType,
} from "services/KnowfullyAdminClient";
import importsUploadStore, { importFile } from "stores/importsUploadStore";
import { Color } from "types/colors";
import { Context, Severity, SnackbarMessageProps } from "types/common-types";

type ImportsFormValues = {
  migrationJobTypeId?: string | null;
  organizationId?: string;
};

const { Form } = withTypes<ImportsFormValues>();

const styles = {
  card: {
    width: "100%",
    margin: "20px 0 20px 0",
    backgroundColor: "#eafbea",
  },
};

const ImportsUploadForm = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const [emptyFile, setEmptyFile] = useState<boolean | null>(null);

  const { columns } = importsUploadStore.useState((s) => {
    setEmptyFile(s.data?.length === 0);
    return {
      columns: s.columns,
    };
  });

  const [selectedJobType, setSelectedJobType] = useState<ImportJobType | null>(
    null
  );

  const {
    migrationJobService,
    importsColumnMappingUrl,
    importsUrl,
  } = useGlobalContext();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const navigate = useNavigate();

  const [file, setFile] = useState<FileObject | null>(null);

  useTitle(t({ id: "importsStep1Label" }));

  const fetchedMigrationJobTypes = useQuery<ListResponseOfImportJobType>(
    "migrationJobTypes",
    () =>
      migrationJobService.getImportJobTypes(
        new ListRequestOfJobTypeFilter({
          parameters: JobTypeFilter.fromJS({ onlyCsvConfigured: true }),
        })
      )
  );

  useEffect(() => {
    queryClient.removeQueries("migrationJobTypeFields");
  }, [queryClient]);

  return useMemo(() => {
    const onSubmit = (values: ImportsFormValues) => {
      if (!columns || (columns?.length <= 1 && columns[0] === "")) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "emptyColumnsExcelMessage" }),
            Severity.Error
          )
        );
      } else if (columns.includes("")) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "emptyValueColumnMessage" }),
            Severity.Error
          )
        );
      } else {
        navigate(importsColumnMappingUrl(values.migrationJobTypeId!));
      }
    };

    return (
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={8} md={6}>
          <RedirectBackButton
            style={{ marginBottom: "20px" }}
            redirectRoute={importsUrl()}
            labelKey="backToListButtonLabel"
          />
          <Typography variant="h4">
            <T id="importsStep1Label" />
          </Typography>
          <Box mb={2}></Box>
          <ImportsStepper activeStep={0} />
          <Form
            onSubmit={onSubmit}
            render={({ handleSubmit, values, form, submitting }) => {
              const isKglOrg =
                values.organizationId?.toLowerCase() ===
                API_ORGANIZATION_ID.toLowerCase();
              const isValid = !isKglOrg && !!values.migrationJobTypeId;

              return (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Field name="migrationJobTypeId" required>
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <SelectEntity
                              fetchedData={fetchedMigrationJobTypes}
                              labelField="name"
                              label={t({ id: "migrationTypeLabel" })}
                              {...input}
                              parentChange={(id) => {
                                let targetJobType = fetchedMigrationJobTypes?.data?.data?.find(
                                  (x: any) => x.id === id
                                );
                                setSelectedJobType(targetJobType!);
                              }}
                              textFieldProps={{
                                error: meta.error && meta.submitFailed,
                                required: true,
                              }}
                            />
                            {meta.error && meta.submitFailed && (
                              <FormHelperText>{meta.error}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      )}
                    </Field>
                    <ContextOrganizationField
                      values={values}
                      formChange={form.change}
                    />
                  </Grid>

                  <Box mb={3}></Box>
                  {!values.migrationJobTypeId && (
                    <Alert severity="warning">
                      <T id="reminderSelectMigrationType" />
                    </Alert>
                  )}
                  {isKglOrg && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <T id="reminderSelectOrganization" />
                    </Alert>
                  )}

                  {isValid && file == null && (
                    <DropzoneAreaBase
                      acceptedFiles={[
                        ".csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values",
                      ]}
                      maxFileSize={150000000}
                      filesLimit={1}
                      onAdd={async (files: FileObject[]) => {
                        setFile(files[0]);

                        let context = {
                          organizationId: values.organizationId,
                        } as Context;

                        await importFile(context, files[0]);
                      }}
                      fileObjects={[]}
                      showAlerts={["error", "info"]}
                      showPreviews={true}
                      showFileNamesInPreview={true}
                      onDelete={(a, b) => setEmptyFile(null)}
                    />
                  )}

                  {file != null && (
                    <>
                      <Card sx={styles.card} variant="outlined">
                        <CardContent>
                          <Grid container direction="row" alignItems="center">
                            <Grid item>
                              <AttachFileIcon />
                            </Grid>
                            <Grid item>
                              <Typography variant="h5" component="div">
                                File Details
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box mb={2}></Box>
                          <Typography>{file.file.name}</Typography>
                          <Typography>{file.file.type}</Typography>
                          <Typography>{`${file.file.size} kb`}</Typography>
                        </CardContent>
                        <CardActions>
                          <IconButton onClick={() => setFile(null)}>
                            <ClearIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                      <DialogActions>
                        {submitting && <CircularProgress size={24} />}
                        {!emptyFile && (
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            style={{ backgroundColor: Color.MediumSeaGreen }}
                            endIcon={<ListIcon />}
                            disabled={submitting || !isValid}
                            data-cy="goToFileDetailsButton"
                          >
                            {t({ id: "goToFileDetails" })}
                          </Button>
                        )}
                      </DialogActions>
                      <SnackbarMessage
                        snackbarMessage={snackbarMessage}
                        setSnackbarMessage={setSnackbarMessage}
                      />
                    </>
                  )}
                </form>
              );
            }}
          />
        </Grid>
        <Grid>
          {selectedJobType && (
            <Box my={4}>
              <Alert severity="info">
                <T id="csvFileInfo" />
                <Button
                  style={{ marginLeft: "10px" }}
                  variant="contained"
                  component={Link}
                  to={`/csv_samples/import/${selectedJobType?.name}.csv`}
                  download
                  target="_blank"
                  startIcon={<CloudDownload />}
                  data-cy="downloadSampleButton"
                >
                  <T id="csvFileDownload" />
                </Button>
              </Alert>
            </Box>
          )}
          {emptyFile && (
            <Box my={4}>
              <Alert severity="error">
                <T id="csvEmptyMessage" />
              </Alert>
            </Box>
          )}
        </Grid>
      </Grid>
    );
  }, [
    columns,
    importsColumnMappingUrl,
    importsUrl,
    emptyFile,
    fetchedMigrationJobTypes,
    file,
    navigate,
    selectedJobType,
    snackbarMessage,
    t,
  ]);
};

export default ImportsUploadForm;

